import styled, { keyframes } from 'styled-components';

import { GRADIENTS } from '../../../../theme/tokens';

/**
 * Card-scoped reuse of the Lumio glow waves (Home "Important Now" → LumioWaves),
 * tuned for the wide "king of stage" Business Value hero. The soft wave is a TILED
 * data-URI SVG (one seamless period, real wave geometry + blur) drifted with
 * `background-position` on a normal, non-GPU-promoted element. It is deliberately NOT
 * a 400%-wide transform layer: that needs a huge composited texture which Safari
 * culls (the topmost layer blinks out). background-position is a paint, not a
 * composited layer, so there is nothing to drop. See waves.ts (BOTTOM_WAVE_TILES /
 * waveTileImage).
 */
/** On-screen width of one wave period; the drift scrolls by exactly one tile so the
 *  repeated background loops with no seam. */
const TILE_PX = 920;
const waveScroll = keyframes`
  from { background-position-x: 0; }
  to { background-position-x: -${TILE_PX}px; }
`;
const waveScrollReverse = keyframes`
  from { background-position-x: -${TILE_PX}px; }
  to { background-position-x: 0; }
`;

/** Bottom band that clips + screen-blends the wave layers up from the card foot —
 *  the violet line then reads as the bright crest riding on top of the glow. The
 *  band height is configurable via `$height` (see WAVE_BAND_HEIGHT in the .tsx):
 *  100% makes the glow as tall as the card; lower values hug the foot. */
export const WaveBand = styled.div<{ $height: string; $intensity?: number }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: ${(p) => p.$height};
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
  mix-blend-mode: screen;
  border-radius: inherit;
  /* Clean blend group for the screen-blend (harmless; the Safari flicker fix lives
   * on WaveLayer — see the file header). */
  isolation: isolate;

  /* Light card: screen-blend can't show on white — multiply so the glow tints it,
   * kept FAINT so the figure + chart stay easily readable over it. The base 0.4 wash
   * scales with $intensity (the Calibrating card asks for 50% more vivid), clamped ≤1. */
  :root[data-ows-theme='light'] & {
    mix-blend-mode: multiply;
    opacity: ${(p) => Math.min(1, 0.4 * (p.$intensity ?? 1))};
  }
`;

export const WaveLayer = styled.div<{ $img: string; $dur: number; $reverse?: boolean; $blur: string; $opacity: number; $delay?: number }>`
  position: absolute;
  inset: 0;
  background-image: ${(p) => p.$img};
  background-repeat: repeat-x;
  background-position-x: 0;
  background-position-y: bottom;
  background-size: ${TILE_PX}px 100%;
  opacity: ${(p) => p.$opacity};
  filter: blur(${(p) => p.$blur});
  animation: ${(p) => (p.$reverse ? waveScrollReverse : waveScroll)} ${(p) => p.$dur}ms linear infinite;
  animation-delay: ${(p) => -(p.$delay ?? 0)}ms;

  @media (prefers-reduced-motion: reduce) {
    /* Hold a static frame — colour without perpetual motion. */
    animation: none;
    background-position-x: -${Math.round(TILE_PX * 0.37)}px;
  }
`;

/* ── Shooting stars ───────────────────────────────────────────────────────────
 * A subtle accent over the chart side of the card: faint cyan→white streaks that
 * shoot up on a shallow ~15° diagonal (up-and-right) — distinct from the home
 * banner's steeper -53° dart — then rest invisibly for the remainder of their cycle
 * so they read as occasional shooting stars, not a constant stream. Screen-blended,
 * behind the figure + chart, hidden under reduced-motion. rotate(-75deg) leans the
 * bright head 15° off vertical; the travel vector (78, -290) matches that heading
 * (~300px total), so the head leads and the cyan tail trails. */
const shoot = keyframes`
  0% {
    opacity: 0;
    transform: translate3d(0, 0, 0) rotate(-75deg);
  }
  6% {
    opacity: var(--star-peak, 0.4);
  }
  20% {
    opacity: 0;
    transform: translate3d(78px, -290px, 0) rotate(-75deg);
  }
  100% {
    opacity: 0;
    transform: translate3d(78px, -290px, 0) rotate(-75deg);
  }
`;

/** Right-side band that hosts the streaks (screen-blended, behind content). Clips
 *  the streaks to itself so they never paint past the card's rounded corners. */
export const StarField = styled.div<{ $inset?: string }>`
  position: absolute;
  inset: ${(p) => p.$inset ?? '0 0 0 34%'};
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
  mix-blend-mode: screen;
  isolation: isolate; /* Safari: scope the blend group so the streaks don't flicker (see WaveBand). */

  /* Light theme: multiply so the saturated streak shows on the white card. */
  :root[data-ows-theme='light'] & {
    mix-blend-mode: multiply;
  }

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

export const Star = styled.span<{ $top: string; $left: string; $len: number; $dur: number; $delay: number; $peak: number }>`
  position: absolute;
  top: ${(p) => p.$top};
  left: ${(p) => p.$left};
  width: ${(p) => p.$len}px;
  height: 1.5px;
  border-radius: 2px;
  --star-peak: ${(p) => p.$peak};
  background: ${GRADIENTS.pulseStreak};
  opacity: 0;
  will-change: transform, opacity;
  /* Shallow ~15°-off-vertical streak (head up-right); home banner uses rotate(-53deg). */
  transform: rotate(-75deg);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  animation: ${shoot} ${(p) => p.$dur}ms linear infinite;
  animation-delay: -${(p) => p.$delay}ms;
`;
